/**
 * **What a session shows under its ring**: the client's own words about it, and only the exercises
 * that went differently from the plan.
 *
 * Owner, 24 September 2026: the reflection and the exercises that deviated used to sit behind
 * „Zobacz sesję", and half the card stood empty above the note field. The card now carries them,
 * with the rest of the session folded into one line ("+3 ćwiczenia bez zmian") — the trainer's
 * reading is "what should I say about this session", and an exercise done exactly as prescribed is
 * not part of the answer.
 *
 * The same read also answers an ABANDONED session: `GET /workout-execution/{id}/display` has no
 * completion gate, so the half that was done renders, and every exercise is listed because where
 * the client stopped IS the answer there.
 *
 * ── One mark per set, from the set's own flags ───────────────────────────────────────────────
 * A skipped set is `skipped`; a set that set a record is `record` (any of the six PR flags); a set
 * logged lighter or heavier than its own prescription is `down` / `up`; a completed set with none
 * of those is `done`; anything else has simply not been done. The priority is the one the chips
 * already use: a record outranks a load change, and a skip outranks everything, because it is the
 * thing the trainer most needs to see.
 *
 * Pure: no React, no DOM, no query hook, no i18next. The figure's number formatter arrives as a
 * parameter.
 */

import type {
  TrainWithMeModulesTrainingDtosResponsesDisplayExercise as DisplayExercise,
  TrainWithMeModulesTrainingDtosResponsesDisplaySet as DisplaySet,
  TrainWithMeModulesTrainingDtosResponsesWorkoutExecutionDisplayResponse as WorkoutExecutionDisplay,
} from '~/shared/api/generated/model';
import { setTypeCountsAsWork } from '~/shared/lib/setTypeBadge';

export type SetMark = 'done' | 'skipped' | 'down' | 'up' | 'record' | 'open';

/** What an exercise's line says at its right-hand end. */
export type ExerciseVerdict = 'record' | 'down' | 'up' | 'skipped' | 'notStarted' | 'asPlanned';

/** A movement's best set: its load and its reps, either of which the movement may not measure. */
export interface TopSet {
  readonly weightKg: number | null;
  readonly reps: number | null;
}

/** What the session's verdict was counted from — the server's own tallies, never recounted here. */
export interface SessionTallies {
  readonly setsDone: number;
  readonly setsTotal: number;
  /** Sets logged heavier than their prescription. */
  readonly heavier: number;
  readonly skipped: number;
}

export interface ExerciseLine {
  readonly key: string;
  /** The movement in the library, or `null` for one the client added on their own. */
  readonly exerciseId: number | null;
  readonly name: string;
  /** Counted sets only — a warm-up is not part of the prescription and gets no mark. */
  readonly marks: readonly SetMark[];
  readonly verdict: ExerciseVerdict;
  /**
   * "4 × 87,5" — completed sets by the heaviest load among them, or by the most reps where nothing
   * was loaded. `null` where no set was completed, which the line words as "nie zaczęte".
   */
  readonly figure: string | null;
  /** The best set of this session and of the one before it (`vsLastTime`); `null` where unknown. */
  readonly top: TopSet | null;
  readonly priorTop: TopSet | null;
}

export interface SessionDetail {
  /** The client's reflection on the session, or `null` where they wrote none. */
  readonly reflection: string | null;
  readonly sleepHours: number | null;
  /** The exercises that went differently from the plan, in session order. */
  readonly deviations: readonly ExerciseLine[];
  /** How many exercises went exactly as prescribed. */
  readonly unchanged: number;
  /** Every exercise, in session order — the abandoned card's list. */
  readonly all: readonly ExerciseLine[];
  /** `null` where the read carried no verdict inputs. */
  readonly tallies: SessionTallies | null;
}

function isCounted(set: DisplaySet): boolean {
  return setTypeCountsAsWork(set.setType);
}

function markOf(set: DisplaySet): SetMark {
  if (set.skipped === true) return 'skipped';
  if (set.isCompleted !== true) return 'open';
  if (
    set.isWeightPr === true ||
    set.isE1rmPr === true ||
    set.isSetVolumePr === true ||
    set.isDurationPr === true ||
    set.isDistancePr === true ||
    set.isRoundsPr === true
  ) {
    return 'record';
  }
  if (set.isWeightDown === true) return 'down';
  if (set.isWeightUp === true) return 'up';
  return 'done';
}

function verdictOf(marks: readonly SetMark[], exercise: DisplayExercise): ExerciseVerdict {
  if (exercise.isSkipped === true || marks.includes('skipped')) return 'skipped';
  if (marks.length > 0 && marks.every((mark) => mark === 'open')) return 'notStarted';
  if (marks.includes('record')) return 'record';
  if (marks.includes('down')) return 'down';
  if (marks.includes('up')) return 'up';
  return 'asPlanned';
}

function figureOf(sets: readonly DisplaySet[], formatNumber: (value: number) => string): string | null {
  const completed = sets.filter((set) => set.isCompleted === true && set.skipped !== true);
  if (completed.length === 0) return null;
  const heaviest = Math.max(...completed.map((set) => set.weight ?? 0));
  if (heaviest > 0) return `${String(completed.length)} × ${formatNumber(heaviest)}`;
  const mostReps = Math.max(...completed.map((set) => set.reps ?? 0));
  return mostReps > 0 ? `${String(completed.length)} × ${String(mostReps)}` : String(completed.length);
}

function topSet(top: { weight?: number | null; reps?: number | null } | null | undefined): TopSet | null {
  if (top == null) return null;
  const weightKg = typeof top.weight === 'number' ? top.weight : null;
  const reps = typeof top.reps === 'number' ? top.reps : null;
  return weightKg === null && reps === null ? null : { weightKg, reps };
}

function talliesOf(display: WorkoutExecutionDisplay): SessionTallies | null {
  const inputs = display.verdictInputs;
  if (inputs == null) return null;
  return {
    setsDone: inputs.setsDone ?? 0,
    setsTotal: inputs.setsTotal ?? 0,
    heavier: inputs.weightUpCount ?? 0,
    skipped: inputs.skippedCount ?? 0,
  };
}

export function sessionDetail(
  display: WorkoutExecutionDisplay,
  formatNumber: (value: number) => string,
): SessionDetail {
  const all: ExerciseLine[] = [];
  for (const exercise of display.exercises ?? []) {
    const counted = (exercise.sets ?? []).filter(isCounted);
    const marks = counted.map(markOf);
    all.push({
      key: String(exercise.exerciseExecutionId ?? `${String(exercise.order ?? all.length)}`),
      exerciseId: typeof exercise.exerciseId === 'number' ? exercise.exerciseId : null,
      name: exercise.exerciseName?.trim() ?? '',
      marks,
      verdict: verdictOf(marks, exercise),
      figure: figureOf(counted, formatNumber),
      top: topSet(exercise.vsLastTime?.currentTop),
      priorTop: topSet(exercise.vsLastTime?.priorTop),
    });
  }
  const deviations = all.filter((line) => line.verdict !== 'asPlanned');
  const reflection = display.reflection?.trim();
  const sleep = display.context?.sleepHours;

  return {
    reflection: reflection === undefined || reflection === '' ? null : reflection,
    sleepHours: typeof sleep === 'number' && Number.isFinite(sleep) ? sleep : null,
    deviations,
    unchanged: all.length - deviations.length,
    all,
    tallies: talliesOf(display),
  };
}
